import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Paperclip, Download, Trash2, Upload } from 'lucide-react';

// Round 6: reusable attachment list + upload for invoices, clients,
// sales orders and quotations. Files are stored (base64) on the server
// in the attachments collection, scoped to one record and one company.
const ACCEPTED = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.xls,.docx,.doc,.txt,.csv';
const MAX_BYTES = 10 * 1024 * 1024;

function formatSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

export default function AttachmentsSection({ entityType, entityId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await api.get('/attachments', { params: { entity_type: entityType, entity_id: entityId } });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // Keep the host view usable even if attachments fail to load.
      console.error('Failed to load attachments:', e);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error('File is larger than the 10 MB limit.');
      return;
    }
    const form = new FormData();
    form.append('entity_type', entityType);
    form.append('entity_id', entityId);
    form.append('file', file);
    setUploading(true);
    try {
      await api.post('/attachments/upload', form);
      toast.success('Attachment uploaded');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download attachment');
    }
  };

  const handleDelete = async (att) => {
    try {
      await api.delete(`/attachments/${att.id}`);
      toast.success('Attachment deleted');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete attachment');
    }
  };

  if (!entityId) {
    return (
      <div className="border rounded-lg p-4 mt-4">
        <h4 className="font-medium flex items-center gap-2"><Paperclip className="h-4 w-4" /> Attachments</h4>
        <p className="text-sm text-gray-500 mt-1">Save this record first, then attach files here.</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 mt-4" data-testid="attachments-section">
      <h4 className="font-medium flex items-center gap-2"><Paperclip className="h-4 w-4" /> Attachments ({items.length})</h4>
      <p className="text-xs text-gray-500 mt-1">PDF, images, xlsx, docx and other business files, up to 10 MB each.</p>
      {loading ? (
        <p className="text-sm text-gray-500 mt-3">Loading attachments...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 mt-3">No attachments yet.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((att) => (
            <li key={att.id} className="flex items-center justify-between gap-2 text-sm border rounded px-3 py-2">
              <span className="min-w-0">
                <span className="font-medium truncate block">{att.filename}</span>
                <span className="text-xs text-gray-500">{formatSize(att.size)} - {att.created_at ? new Date(att.created_at).toLocaleString() : ''} - {att.uploaded_by || 'Unknown'}</span>
              </span>
              <span className="flex gap-1 shrink-0">
                <Button type="button" variant="outline" size="sm" onClick={() => handleDownload(att)} title="Download"><Download className="h-4 w-4" /></Button>
          <Button type="button" variant={confirmingId === att.id ? "destructive" : "outline"} size="sm" onClick={() => confirmingId === att.id ? handleDelete(att) : setConfirmingId(att.id)} title={confirmingId === att.id ? "Click again to confirm delete" : "Delete"}>{confirmingId === att.id ? "Confirm delete?" : <Trash2 className="h-4 w-4" />}</Button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex items-center gap-2">
        <Input type="file" accept={ACCEPTED} onChange={handleUpload} disabled={uploading} className="max-w-md" data-testid="attachment-file-input" />
        <span className="text-xs text-gray-500 flex items-center gap-1"><Upload className="h-3 w-3" /> {uploading ? 'Uploading...' : ''}</span>
      </div>
    </div>
  );
}
